/**
 * @fileoverview Shared run-flow navigation helpers and canonical step list.
 * @module import-editor/runFlow/navigation
 *
 * Responsibilities:
 * - Define the canonical run-intent step descriptors (id + label +
 *   description) and the dataMode-aware visibility / next / prev rules.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for the ImportDataMode type
 *
 * Architecture:
 * - Pure functions and constants — no React, no state. Consumed by the
 *   shared run flow (`UnifiedImportRunFlow`) and unit tests.
 *
 * Notes:
 * - The canonical run-intent pipeline is:
 *
 *     upload → columnMapping → matchKey → preview → commit
 *
 * Lookup-mode imports skip the matchKey step (composite key, fixed
 * behavior). This helper centralizes that branching so both legacy
 * containers (and eventually the shared UnifiedImportWizard run flow)
 * share a single source of truth. Adding a new dataMode-dependent skip
 * in the future only needs to happen here.
 */

import type { ImportDataMode } from '@firesquirrel/local-db';

export type RunStepId = 'upload' | 'columnMapping' | 'matchKey' | 'preview' | 'commit';

export interface RunStepDescriptor {
  id: RunStepId;
  label: string;
  /** Longer subtitle shown under the stepper when the host chrome enables
      step descriptions (tenant-web run wizard). */
  description?: string;
}

export const RUN_STEPS: RunStepDescriptor[] = [
  { id: 'upload',        label: 'Template & File', description: 'Name this import, select a template, and upload your data file.' },
  { id: 'columnMapping', label: 'Map Columns',     description: 'Confirm how file columns map to entity fields.' },
  { id: 'matchKey',      label: 'Match Key',       description: 'Choose how existing records are matched and set import behavior.' },
  { id: 'preview',       label: 'Dry Run',         description: 'Preview which records will be created, updated, or skipped.' },
  { id: 'commit',        label: 'Commit',          description: 'Confirm and execute the import.' },
];

/**
 * Compute the visible steps for the current dataMode. Only lookup mode
 * strips a step today (matchKey), but the helper is intentionally
 * dataMode-keyed rather than isLookup-boolean so future modes can hook
 * in without rippling through callers.
 */
export function getVisibleRunSteps(
  dataMode: ImportDataMode | null | undefined,
): RunStepDescriptor[] {
  if (dataMode === 'lookup') {
    return RUN_STEPS.filter(s => s.id !== 'matchKey');
  }
  return RUN_STEPS;
}

/**
 * Resolve the next run step accounting for dataMode-based skips.
 * Returns null when the current step is already the last visible step
 * (caller decides whether to treat that as "go to Review", "finalize",
 * or a no-op — the helper stays pure).
 */
export function getNextRunStep(
  current: RunStepId,
  dataMode: ImportDataMode | null | undefined,
): RunStepId | null {
  const visible = getVisibleRunSteps(dataMode);
  const idx = visible.findIndex(s => s.id === current);
  if (idx === -1 || idx === visible.length - 1) return null;
  return visible[idx + 1]!.id;
}

/** Mirror of `getNextRunStep` for the Previous button. */
export function getPrevRunStep(
  current: RunStepId,
  dataMode: ImportDataMode | null | undefined,
): RunStepId | null {
  const visible = getVisibleRunSteps(dataMode);
  const idx = visible.findIndex(s => s.id === current);
  if (idx <= 0) return null;
  return visible[idx - 1]!.id;
}
